import { Icon } from '@design/components/icon/Icon';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipTrigger } from '../tooltip/Tooltip';
import { NavItem } from './components/nav-item/NavItem';
import styles from './SideNavigation.module.scss';

export interface SideNavigationProps {
  /** Key of the currently-active sub-item; passed down to each `NavItem` for highlighting. */
  activeSubKey?: string;
  /** Top-level category data: title, items, optional bottom-pinned items, and the "create new" handler. */
  data: Category;
  /** Extra class forwarded to the `<aside>`. */
  className?: string;
  /** Whether the sidebar is expanded (showing labels) or collapsed (icons only). Defaults to `true`. */
  isExpanded?: boolean;
  /** Called when the user clicks the expand/collapse toggle. */
  onSidebarToggle?: () => void;
  /** Show the expand/collapse toggle. Defaults to `true`. */
  expandable?: boolean;
  /** Show the horizontal separator between the action button and the items list. Defaults to `true`. */
  showSeparator?: boolean;
  /** Show the "create new" action button (text in expanded mode, plus icon in collapsed mode). Defaults to `true`. */
  createNewButton?: boolean;
}

/** A single navigation item rendered inside `SideNavigation`. */
export interface CategoryItem {
  /** Visible label. */
  label: string;
  /** Stable key. Compared against `activeSubKey` to determine the active item. */
  key: string;
  /** Optional leading icon (any `ReactNode`, typically an `<Icon>`). */
  icon?: ReactNode;
  /** Optional trailing badge (e.g. unread count). */
  badge?: ReactNode;
  /** When true, the item is rendered as disabled and ignores clicks. */
  disabled?: boolean;
  /** Called with the item when it's clicked. */
  onClick: (item: CategoryItem) => void;
  /** Render a divider line below this item. */
  borderBottom?: boolean;
  /** Optional notification dot/badge on the icon (for collapsed-mode signals). */
  notification?: ReactNode;
}

/** Top-level category passed to `SideNavigation` as `data`. */
export interface Category {
  /** Optional id for the category. */
  id?: string;
  /** Visible header text rendered above the items in expanded mode. */
  title?: string;
  /** Stable key for the category. */
  key: string;
  /** Label for the "create new" action button. When omitted, the button is hidden in expanded mode. */
  createNewLabel?: string;
  /** Items rendered in the main list. */
  items: CategoryItem[];
  /** Items pinned to the bottom of the sidebar (e.g. settings, sign-out). */
  bottomItems?: CategoryItem[];
  /** Reserved (currently consumed via the `SideNavigationProps` flag of the same name). */
  expandable?: boolean;
  /** Reserved (consumed via the `SideNavigationProps` flag of the same name). */
  showSeparator?: boolean;
  /** Reserved (consumed via the `SideNavigationProps` flag of the same name). */
  createNewButton?: boolean;
  /** Called when the "create new" button is clicked. */
  onClick?: () => void;
}

/**
 * Collapsible side rail for primary in-app navigation.
 *
 * Two modes: expanded (labels + icons) and collapsed (icons only with
 * tooltips). Items can carry icons, badges, and disabled states. A
 * "create new" action button is rendered above the list, and pinned
 * items can be placed at the bottom via `data.bottomItems`.
 */
export const SideNavigation = ({
  activeSubKey,
  data,
  className,
  isExpanded = true,
  onSidebarToggle,
  expandable = true,
  showSeparator = true,
  createNewButton = true,
}: SideNavigationProps) => {
  const { t } = useTranslation('common');
  const createNewLabel = data.createNewLabel;

  return (
    <aside
      className={clsx(styles.asideExpandable, className, {
        [styles.isExpanded]: isExpanded,
      })}
      data-testid="side-navigation"
    >
      <div className={styles.toggleButtonWrapper}>
        {isExpanded && <header className={styles.header}>{data.title}</header>}
        {expandable && (
          <Tooltip
            label={
              isExpanded
                ? t('common:sideNavigation.hideMenu')
                : t('common:sideNavigation.showMenu')
            }
            trigger={TooltipTrigger.Hover}
            placement="right"
          >
            <button
              className={clsx(styles.toggleButton, {
                [styles.isExpanded]: isExpanded,
              })}
              onClick={onSidebarToggle}
              data-testid={'button-toggle'}
            >
              <Icon iconName="ArrowRight" size={16} />
            </button>
          </Tooltip>
        )}
      </div>

      {!isExpanded && createNewButton && (
        <Tooltip
          label={createNewLabel}
          trigger={TooltipTrigger.Hover}
          placement="right"
        >
          <button
            className={styles.actionButton}
            onClick={data.onClick}
            data-testid={'sidebar-add-button'}
          >
            <Icon iconName="Add" size={16} />
          </button>
        </Tooltip>
      )}

      {isExpanded && createNewLabel && createNewButton && (
        <button
          className={styles.actionButtonWrapper}
          onClick={data.onClick}
          data-testid={'sidebar-add-button-expanded'}
        >
          <Icon iconName="Add" size={16} />
          {createNewLabel}
        </button>
      )}

      {showSeparator && <hr className={styles.separator} />}

      <div
        className={clsx(styles.categories, {
          [styles.isExpanded]: isExpanded,
        })}
      >
        {data.items.map(item => {
          return (
            <NavItem
              key={item.key}
              item={item}
              activeSubKey={activeSubKey}
              sideBarExpanded={isExpanded}
            />
          );
        })}
      </div>

      <div
        className={clsx(styles.categories, styles.bottom, {
          [styles.isExpanded]: isExpanded,
        })}
      >
        {data.bottomItems?.map(item => {
          return (
            <NavItem
              key={item.key}
              item={item}
              activeSubKey={activeSubKey}
              sideBarExpanded={isExpanded}
            />
          );
        })}
      </div>
    </aside>
  );
};
